import { ApiResponse } from "apisauce";

import { ReportRunFact } from "../interface/ReportRunFact";
import { ReportRunResult } from "../interface/ReportRunResult";

export const runningResult = (headline: string): ReportRunResult => ({
  status: "running",
  headline,
});

interface EmailedReportOptions {
  /** Where the workbook or PDF was sent. */
  recipient?: string;
  /** Said when the report found nothing in the window it was given. */
  emptyMessage: string;
  /** What the run was asked for — period, stream, date. */
  facts?: ReportRunFact[];
}

const serverMessage = (response: ApiResponse<unknown>, fallback: string) =>
  typeof response.data === "string" && response.data.trim().length > 0
    ? response.data
    : fallback;

/**
 * The outcome of a report that is generated on the server and emailed out. All four of
 * them answer the same three questions — did it run, did it find anything, where did it
 * go — so they answer them in one place rather than four slightly different toasts.
 */
export const emailedReportResult = (
  response: ApiResponse<unknown>,
  { recipient, emptyMessage, facts = [] }: EmailedReportOptions
): ReportRunResult => {
  const asked = facts.length > 0 ? [{ title: "Report", facts }] : [];

  if (response.status === 204) {
    return {
      status: "empty",
      headline: emptyMessage,
      note: "Nothing was emailed. Check the dates, then try again.",
      sections: asked,
    };
  }

  if (response.ok) {
    return {
      status: "success",
      headline: recipient
        ? `Generated and emailed to ${recipient}.`
        : "Generated and emailed.",
      note: "Large reports can take a few minutes to arrive.",
      sections: asked,
    };
  }

  return {
    status: "error",
    headline: serverMessage(response, "The report could not be generated."),
    note: `The server replied ${response.status ?? "with no status"}. Nothing was emailed.`,
    sections: asked,
  };
};

/** A request that threw before the server could answer. */
export const failedReportResult = (error: unknown): ReportRunResult => ({
  status: "error",
  headline: "The report could not be generated.",
  note: error instanceof Error ? error.message : "The request did not complete.",
});
